import React, { useMemo } from 'react';
import styled from 'styled-components';
import ConceptLinkerAiLinkerItemsModel from 'models/ConceptLinkerAiLinkerItemsModel';
import {
  ConceptLinkerAiLinkerItemStatus,
  IConceptLinkerAiLinkerItem,
  IConceptLinkerAiLinkerDialectResult,
} from 'types/conceptLinkerAiLinker';

interface IProps {
  itemsModel: ConceptLinkerAiLinkerItemsModel;
  onFilterChange: (type: 'status', value: string) => void;
  activeFilter: { type: 'status'; value: string } | null;
  onClearFilter: () => void;
}

function hasDialectDifferences(dialectResults: IConceptLinkerAiLinkerDialectResult[]): boolean {
  // Compare concept sets per phrase order + position across dialects
  const dialectConceptMaps = dialectResults.map((dr) => {
    const map: Record<string, string> = {}; // "phraseOrder-pos" → "ref:refId"
    (dr.phrases || []).forEach((phrase) => {
      (phrase.concepts || []).forEach((c) => {
        c.positions.forEach((pos) => {
          map[`${phrase.order}-${pos}`] = `${c.ref}:${c.refId}`;
        });
      });
    });
    return map;
  });

  // Collect all keys
  const allKeys = new Set<string>();
  dialectConceptMaps.forEach((m) => Object.keys(m).forEach((k) => allKeys.add(k)));

  for (const key of allKeys) {
    const values = dialectConceptMaps.map((m) => m[key]);
    const unique = new Set(values.filter((v) => v !== undefined));
    if (unique.size > 1 || (unique.size === 1 && values.some((v) => v === undefined))) {
      return true;
    }
  }
  return false;
}

const COLORS = {
  processing: '#9C27B0',
  needsReview: '#ff9800',
  reviewed: '#4caf50',
  error: '#E53935',
  differences: '#7B1FA2',
};

const ConceptLinkerAiLinkerSummary: React.FC<IProps> = ({
  itemsModel,
  onFilterChange,
  activeFilter,
  onClearFilter,
}) => {
  const items = (itemsModel.gridData as IConceptLinkerAiLinkerItem[]) || [];

  const { summary, completedCounts } = useMemo(() => {
    const s = {
      total: items.length,
      pending: 0,
      needsReview: 0,
      reviewed: 0,
      error: 0,
      withDifferences: 0,
    };

    items.forEach((item) => {
      switch (item.status) {
        case ConceptLinkerAiLinkerItemStatus.PENDING:
          s.pending++;
          break;
        case ConceptLinkerAiLinkerItemStatus.NEEDS_REVIEW:
          s.needsReview++;
          break;
        case ConceptLinkerAiLinkerItemStatus.DONE:
          s.reviewed++;
          break;
        case ConceptLinkerAiLinkerItemStatus.ERROR:
          s.error++;
          break;
      }

      // Check for dialect differences
      if (item.ai_result?.dialects) {
        const dialectEntries = Object.values(item.ai_result.dialects);
        if (dialectEntries.length > 1) {
          const hasDiff = hasDialectDifferences(dialectEntries);
          if (hasDiff) s.withDifferences++;
        }
      }
    });

    return {
      summary: s,
      completedCounts: {
        needsReview: s.needsReview,
        reviewed: s.reviewed,
        error: s.error,
      },
    };
  }, [items]);

  if (summary.total === 0) {
    return null;
  }

  const isActive = (type: string, value: string) =>
    activeFilter?.type === type && activeFilter?.value === value;

  // Progress bar segments
  const allSegments = [
    { key: 'needsReview', width: (completedCounts.needsReview / summary.total) * 100, color: COLORS.needsReview },
    { key: 'reviewed', width: (completedCounts.reviewed / summary.total) * 100, color: COLORS.reviewed },
    { key: 'error', width: (completedCounts.error / summary.total) * 100, color: COLORS.error },
  ].filter((s) => s.width > 0);

  let cumulativeOffset = 0;
  const segmentsWithOffset = allSegments.map((s, index) => {
    const segment = { ...s, offset: cumulativeOffset };
    cumulativeOffset += s.width;

    let position: 'first' | 'middle' | 'last' | 'only' = 'middle';
    if (allSegments.length === 1) position = 'only';
    else if (index === 0) position = 'first';
    else if (index === allSegments.length - 1) position = 'last';

    return { ...segment, position };
  });

  return (
    <Container>
      <ProgressBar>
        {summary.pending > 0 ? (
          <ProgressFill $width={100} $isProcessing={true} />
        ) : (
          <>
            {segmentsWithOffset.map((segment) => (
              <ProgressSegment
                key={segment.key}
                $width={segment.width}
                $color={segment.color}
                $position={segment.position}
                $offset={segment.offset}
              />
            ))}
          </>
        )}
      </ProgressBar>

      <StatsCard>
        <StatItem $clickable={true} onClick={onClearFilter} className={!activeFilter ? 'active' : ''}>
          <div className="count">{summary.total}</div>
          <div className="label">Total</div>
        </StatItem>

        {summary.pending > 0 && (
          <StatItem
            $color={COLORS.processing}
            $clickable={true}
            onClick={() => onFilterChange('status', 'pending')}
            className={isActive('status', 'pending') ? 'active' : ''}
          >
            <div className="count">{summary.pending}</div>
            <div className="label">Processing</div>
          </StatItem>
        )}

        {summary.needsReview > 0 && (
          <StatItem
            $color={COLORS.needsReview}
            $clickable={true}
            onClick={() => onFilterChange('status', 'needs_review')}
            className={isActive('status', 'needs_review') ? 'active' : ''}
          >
            <div className="count">{summary.needsReview}</div>
            <div className="label">Needs Review</div>
          </StatItem>
        )}

        {summary.reviewed > 0 && (
          <StatItem
            $color={COLORS.reviewed}
            $clickable={true}
            onClick={() => onFilterChange('status', 'reviewed')}
            className={isActive('status', 'reviewed') ? 'active' : ''}
          >
            <div className="count">{summary.reviewed}</div>
            <div className="label">Reviewed</div>
          </StatItem>
        )}

        {summary.error > 0 && (
          <StatItem
            $color={COLORS.error}
            $clickable={true}
            onClick={() => onFilterChange('status', 'error')}
            className={isActive('status', 'error') ? 'active' : ''}
          >
            <div className="count">{summary.error}</div>
            <div className="label">Error</div>
          </StatItem>
        )}

        {summary.withDifferences > 0 && (
          <StatItem
            $color={COLORS.differences}
            $clickable={true}
            onClick={() => onFilterChange('status', 'differences')}
            className={isActive('status', 'differences') ? 'active' : ''}
          >
            <div className="count">{summary.withDifferences}</div>
            <div className="label">With Differences</div>
          </StatItem>
        )}
      </StatsCard>
    </Container>
  );
};

export default ConceptLinkerAiLinkerSummary;

const Container = styled.div`
  margin-bottom: 8px;
`;

const StatsCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const StatItem = styled.div<{ $color?: string; $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${(props) => (props.$clickable ? 'rgba(0, 0, 0, 0.03)' : 'transparent')};
  }

  &.active {
    background-color: rgba(33, 150, 243, 0.08);
    border-color: #2196f3;
  }

  .count {
    font-size: 28px;
    font-weight: 600;
    color: ${(props) => props.$color || '#333'};
    line-height: 1;
  }

  .label {
    font-size: 14px;
    color: #666;
    margin-top: 6px;
    font-weight: 500;
    text-align: center;
  }
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 0 10px 0;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ProgressFill = styled.div<{ $width: number; $isProcessing?: boolean }>`
  height: 100%;
  width: ${(props) => props.$width}%;
  background: linear-gradient(90deg, #9c27b0 0%, #7b1fa2 50%, #9c27b0 100%);
  transition: width 0.5s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    animation: shimmer 1s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const ProgressSegment = styled.div<{
  $width: number;
  $color: string;
  $position: 'first' | 'middle' | 'last' | 'only';
  $offset?: number;
}>`
  position: absolute;
  height: 100%;
  width: ${(props) => props.$width}%;
  background: ${(props) => props.$color};
  left: ${(props) => props.$offset || 0}%;
  transition: all 0.5s ease;

  ${(props) => {
    if (props.$position === 'only') return 'border-radius: 4px;';
    if (props.$position === 'first') return 'border-radius: 4px 0 0 4px;';
    if (props.$position === 'last') return 'border-radius: 0 4px 4px 0;';
    return '';
  }}
`;
